package com.hyperether.goodjob.scenes.map

import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.neverEqualPolicy
import androidx.lifecycle.viewModelScope
import com.hyperether.goodjob.getPlatformChecker
import com.hyperether.goodjob.log
import com.hyperether.goodjob.map.Marker
import com.hyperether.goodjob.models.Job
import com.hyperether.goodjob.models.Site
import com.hyperether.goodjob.repository.Repository
import com.hyperether.goodjob.repository.remote.model.LatLng
import com.hyperether.goodjob.scenes.JobsViewModel
import kotlinx.coroutines.launch

class MapViewModel(repository: Repository) : JobsViewModel(repository) {

    val markers = mutableStateListOf<Marker>()
    val selectedMarkerPosition = mutableStateOf<LatLng?>(null)
    val selectedJob = mutableStateOf<Job?>(null)
    val centerPosition = mutableStateOf<LatLng?>(null, neverEqualPolicy())
    var mapZoom = mutableStateOf<Boolean?>(null)

    init {
        if (getPlatformChecker().isMobile())
            init()
    }

    fun init() {
        resetFilter()
        initViewModel()
        initMapViewModel()
        if (getPlatformChecker().isMobile()) {
            loadRemoteData()
        }
    }

    fun initMapViewModel() {
        viewModelScope.launch {
            repository.getSitesFlow().collect { sites ->
                sitesMap.clear()
                sitesMap.putAll(sites.filter { site -> site.id != null }
                    .associateBy { site -> site.id!! })
                updateMapMarkers(filteredJobs)
                if (markers.isNotEmpty()) {
                    selectedMarkerPosition.value = LatLng(markers[0].lat, markers[0].lng)
                    centerPosition.value = LatLng(markers[0].lat, markers[0].lng)
                }
            }
        }
    }

    override fun loadRemoteData() {
        super.loadRemoteData()
        viewModelScope.launch {
            repository.getAllContactsByWorkspace()
        }
    }

    override fun filterJobs(
        jobList: List<Job>,
        sitesMap: Map<String, Site>,
        selectedStatus: Set<String>,
        selectedEmployees: Set<String>,
        selectedSchedule: Set<String>,
        selectedLocation: Set<String>
    ): List<Job> {
        val filteredJobsList = mutableListOf<Job>()
        if (selectedStatus.isEmpty() &&
            selectedEmployees.isEmpty() &&
            selectedSchedule.isEmpty() &&
            selectedLocation.isEmpty()
        ) {
            filteredJobsList.addAll(jobList)
        } else {

            val selectedSitesIds = selectedLocation.flatMap { selectedAddress ->
                sitesMap.filterValues { site ->
                    site.addressObject?.address.equals(selectedAddress, ignoreCase = true)
                }.values.map { it.id }
            }
            filteredJobsList.addAll(jobList.filter { job ->
                selectedStatus.any { query ->
                    job.status?.equals(query, ignoreCase = true) == true
                }.or(selectedStatus.isEmpty()) && selectedEmployees.any { query ->
                    job.assignees?.employees?.any { employee ->
                        employee?.getName()?.equals(query, ignoreCase = true) == true
                    } == true || selectedEmployees.any { query ->
                        job.assignees?.teams?.any { team ->
                            team?.name?.equals(query, ignoreCase = true) == true
                        } == true
                    }
                }.or(selectedEmployees.isEmpty()) && selectedSchedule.any { query ->
                    job.startDate?.equals(query, ignoreCase = true) == true
                }.or(selectedSchedule.isEmpty()) && selectedSitesIds.equals(job.contactSiteId)
                    .or(selectedSitesIds.isEmpty())
            })
        }
        return filteredJobsList
    }

    fun searchMapJobs(searchText: String): List<Job> {
        log.d("MAP") { "searchMapJobs: ${searchText}" }
        val filteredJobsSearch = if (searchText.isNotBlank()) {
            filteredJobs.filter { job ->
                job.jobTitle?.contains(searchText, ignoreCase = true) == true ||
                        job.contactSiteName?.contains(
                            searchText, ignoreCase = true
                        ) == true ||
                        (sitesMap.any {
                            it.value.addressObject?.address?.contains(
                                searchText,
                                ignoreCase = true
                            ) == true && it.key == job.id
                        })
            }
        } else {
            filteredJobs
        }
        updateMapMarkers(filteredJobsSearch)
        return filteredJobsSearch
    }

    fun updateMapMarkers(filteredJobs: List<Job>) {
        markers.clear()
        val newMarkers = mutableListOf<Marker>()
        filteredJobs.forEach {
            sitesMap[it.contactSiteId]?.addressObject?.let { addressObject ->
                addressObject.lat?.let { lat ->
                    addressObject.long?.let { lon ->
                        newMarkers.add(
                            Marker(
                                lat, lon, it.jobTitle ?: "", it.status ?: ""
                            )
                        )
                    }
                }
            }
        }

        selectedJob.value?.let { job ->
            val sortedMarkers = newMarkers.sortedWith(
                compareBy { it.title == job.jobTitle })
            markers.addAll(sortedMarkers)
        } ?: run {
            newMarkers.reverse()
            markers.addAll(newMarkers)
        }
        markers.forEach {
            log.d("MAP") { "Selected: ${selectedJob.value?.jobTitle} Sorted Marker: ${it.title}" }
        }
    }

    fun getUserPosition() {
        if (markers.isNotEmpty()) return

        val userAddress = repository.getUser()?.address ?: return

        viewModelScope.launch {
            runCatching {
                repository.findPlace(userAddress)?.firstOrNull()?.location
            }.onSuccess { location ->
                selectedMarkerPosition.value = location
                centerPosition.value = location
            }
        }
    }
}